/** Click and form submit event delegation (data attributes + config rules). */

import { state } from "./state.js";
import { sendEvent } from "./event-transport.js";
import { matchUrl } from "./url-matching.js";
import { captureEmail, capturePhone, captureNamePair, captureFullName } from "./pii-capture.js";
import { reinitPixelUserData } from "./facebook-pixel.js";
import { refreshPlatformPixelUserData } from "./platforms.js";
import { persistUserData } from "./user-data-store.js";
import { shouldSkipForRule } from "./conversion-dedup.js";
import { armPendingSubmission } from "./pending-submission.js";
import {
  DATA_ATTR_EVENT,
  DATA_ATTR_VALUE,
  DATA_ATTR_CURRENCY,
  DATA_ATTR_DETECT_EMAIL,
  DATA_ATTR_MAP,
  DEFAULT_CURRENCY,
  MAX_CLICK_RULE_TEXT_LENGTH,
} from "./constants.js";
import { DATA_ATTR_RULE, DATA_ATTR_RULE_EVENT, DATA_ATTR_RULE_VALUE, DATA_ATTR_RULE_CURRENCY } from "./element-tagging.js";
import type { TrackingRule } from "./types.js";

/** Safely read the value of a form input element (avoids repeated `as HTMLInputElement` casts). */
export function getInputValue(el: Element | null): string {
  if (!el) return "";
  return (el as HTMLInputElement).value || "";
}

/** Find the first input matching common email selector patterns within a container. */
export function findEmailInput(container: Element): Element | null {
  return (
    container.querySelector('input[type="email"]') ||
    container.querySelector('input[name*="email"]') ||
    container.querySelector('input[name*="e-mail"]') ||
    container.querySelector('input[id*="email"]') ||
    container.querySelector('input[name*="your-email"]') ||
    container.querySelector('input[name="billing_email"]')
  );
}

/** Find the first input matching common phone selector patterns within a container. */
export function findPhoneInput(container: Element): Element | null {
  return (
    container.querySelector('input[type="tel"]') ||
    container.querySelector('input[name*="phone"]') ||
    container.querySelector('input[name*="tel"]') ||
    container.querySelector('input[name="billing_phone"]')
  );
}

// ── Native submit dedup (used by ajax-form-detection) ──────────────
const recentNativeSubmits = new Set<Element>();

/** Mark a form as handled by the native submit path. */
export function markNativeSubmit(form: Element): void {
  recentNativeSubmits.add(form);
  setTimeout(() => { recentNativeSubmits.delete(form); }, 50);
}

/** Check if this form was already handled by the native submit path. */
export function wasNativelySubmitted(form: Element): boolean {
  return recentNativeSubmits.has(form);
}

/** Read custom data-tst-* attributes from an element using the declarative DATA_ATTR_MAP. */
function readCustomDataAttributes(el: Element): Record<string, unknown> {
  const customData: Record<string, unknown> = {};
  for (const [attr, config] of Object.entries(DATA_ATTR_MAP)) {
    const rawValue = el.getAttribute(attr);
    if (rawValue == null) continue;
    customData[config.key] = config.parse(rawValue);
  }
  return customData;
}

/** Read data-tst-* attributes from an element and return event data. */
function parseDataAttributes(el: Element): Record<string, unknown> {
  const data: Record<string, unknown> = {
    eventName: el.getAttribute(DATA_ATTR_EVENT),
  };

  const rawValue = el.getAttribute(DATA_ATTR_VALUE);
  if (rawValue) data.eventValue = parseFloat(rawValue);
  data.currency = el.getAttribute(DATA_ATTR_CURRENCY) || DEFAULT_CURRENCY;

  const customData = readCustomDataAttributes(el);
  if (Object.keys(customData).length > 0) data.customData = customData;

  return data;
}

const CLICKABLE_TAGS = ["A", "BUTTON", "SUMMARY", "LABEL"];

/** Text-rules may only match a real click target or a compact element. The
 * ancestor walk in handleClickEvent otherwise reaches <body>, whose
 * innerText is the WHOLE page: any click anywhere would then match any
 * rule text that is visible somewhere on the page (found 2026-07-23). */
function isClickRuleCandidate(el: Element, elText: string): boolean {
  const tag = el.tagName;
  if (tag === "BODY" || tag === "HTML") return false;
  if (CLICKABLE_TAGS.indexOf(tag) !== -1) return true;
  const role = el.getAttribute("role");
  if (role === "button" || role === "link") return true;
  return elText.length <= MAX_CLICK_RULE_TEXT_LENGTH;
}

/** Look up a config rule by id (once-per-visitor state for tagged elements). */
function ruleById(ruleId: string): TrackingRule | null {
  const rules = state.config && state.config.rules;
  if (!rules) return null;
  for (const rule of rules) {
    if (rule.id === ruleId) return rule;
  }
  return null;
}

/** Find a config-based click rule matching the element's text and current URL. */
function findClickRule(el: Element): TrackingRule | null {
  if (!state.config || !state.config.rules) return null;

  // A selector rule matches the element itself, no text involved. Element
  // tagging already covers what was on the page at load; this catches what
  // arrived afterwards, which on a WordPress site is any block rendered by a
  // plugin after our script ran.
  for (const rule of state.config.rules) {
    if (rule.triggerType !== "click" || !rule.cssSelector) continue;
    if (rule.urlPattern && !matchUrl(window.location.href, rule.urlPattern, rule.matchType)) continue;
    try {
      if (el.matches(rule.cssSelector)) return rule;
    } catch { /* invalid selector, nothing to match */ }
  }

  const elText = ((el as HTMLElement).innerText || el.textContent || "")
    .trim()
    .toLowerCase();
  if (!elText) return null;
  if (!isClickRuleCandidate(el, elText)) return null;

  for (const rule of state.config.rules) {
    if (rule.triggerType !== "click" || !rule.elementText) continue;
    if (!elText.includes(rule.elementText.toLowerCase())) continue;
    if (rule.urlPattern && !matchUrl(window.location.href, rule.urlPattern, rule.matchType)) continue;

    return rule;
  }

  return null;
}

/**
 * Forms that are plumbing, not conversions.
 *
 * A form_submit rule with no page filter matches every form on the site, and
 * a shop is full of forms that are not leads. On 6 August 2026 a WooCommerce
 * product page produced a Lead on every add to cart, because the add-to-cart
 * control is a plain `<form class="cart">` that submits natively. A customer
 * running a Lead rule for their contact page would have seen their lead count
 * track their add-to-cart count, and every one of those goes to Meta.
 *
 * Deliberately short. Only forms whose purpose is unmistakable from their own
 * markup are listed; anything arguable stays a conversion, because refusing to
 * count a real lead is the worse mistake.
 */
const NON_CONVERSION_FORM_SELECTOR = [
  "form.cart",                      // WooCommerce add to cart
  'form[action*="add-to-cart"]',
  "form.woocommerce-cart-form",     // cart quantity update
  "form.checkout_coupon",
  'form[role="search"]',
  "form.search-form",
  ".wp-block-search form",
  // The checkout is a Purchase, measured elsewhere, and logging in or
  // registering is not a lead either. Without these a Lead rule with no page
  // filter counted every order and every sign-in.
  "form.checkout",
  "form.woocommerce-checkout",
  "form.woocommerce-form-login",
  "form.woocommerce-form-register",
  "form.lost_reset_password",
].join(",");

export function isNonConversionForm(form: Element): boolean {
  try {
    return form.matches(NON_CONVERSION_FORM_SELECTOR) || !!form.closest(".wp-block-search");
  } catch {
    return false;
  }
}

/** Find a config-based form_submit rule matching the current URL. */
export function findFormSubmitRule(): TrackingRule | null {
  if (!state.config || !state.config.rules) return null;

  for (const rule of state.config.rules) {
    if (rule.triggerType !== "form_submit") continue;
    if (rule.urlPattern && !matchUrl(window.location.href, rule.urlPattern, rule.matchType)) continue;

    return rule;
  }

  return null;
}

/**
 * Extract and hash user data (email, phone) from form fields.
 * Stores hashed values in state.userData and re-inits the browser pixel
 * once all hashes have resolved.
 *
 * Returns a Promise that resolves when all hashing is complete,
 * so callers can await before sending events.
 */
function captureFormUserData(form: HTMLFormElement): Promise<void> {
  const promises: Promise<void>[] = [];

  const emailValue = getInputValue(findEmailInput(form));
  if (emailValue && emailValue.includes("@")) {
    promises.push(captureEmail(emailValue));
  }

  const phoneValue = getInputValue(findPhoneInput(form));
  if (phoneValue) {
    promises.push(capturePhone(phoneValue));
  }

  // Capture first/last name for better Advanced Matching (EN + NL fields)
  const fnEl = form.querySelector('input[name="first_name"], input[name="first-name"], input[name="firstname"], input[name*="fname"], input[autocomplete="given-name"], input[name*="voornaam"], input[name="billing_first_name"]');
  const fnValue = getInputValue(fnEl);
  if (fnValue) {
    const lnEl = form.querySelector('input[name="last_name"], input[name="last-name"], input[name="lastname"], input[name*="lname"], input[name*="surname"], input[autocomplete="family-name"], input[name*="achternaam"], input[name="billing_last_name"]');
    const lnValue = getInputValue(lnEl);
    promises.push(captureNamePair(fnValue, lnValue || null));
  } else {
    // Fallback: single name field (Elementor, CF7 your-name, etc.)
    // Scan text inputs for placeholder OR name containing "name"/"naam"
    // (Dutch placeholders like "Je naam" must match too)
    const excludeWords = ["user", "email", "company", "business", "url", "website", "domain", "search", "password", "gebruiker", "bedrijf", "achternaam", "voornaam"];
    const textInputs = form.querySelectorAll('input[type="text"]');
    for (let i = 0; i < textInputs.length; i++) {
      const input = textInputs[i] as HTMLInputElement;
      const hint = ((input.placeholder || "") + " " + (input.name || "")).toLowerCase();
      if (!hint.includes("name") && !hint.includes("naam")) continue;
      if (excludeWords.some((w) => hint.includes(w))) continue;
      if (input.offsetParent === null && input.style.display === "none") continue;

      const fullName = input.value.trim();
      if (!fullName) break;

      promises.push(captureFullName(fullName));
      break;
    }
  }

  if (promises.length > 0) {
    return Promise.all(promises).then(() => { persistUserData(); reinitPixelUserData(); refreshPlatformPixelUserData(); });
  }
  return Promise.resolve();
}

/** Handle click events — checks tagged rules, data attributes, then config rules. */
function handleClickEvent(e: MouseEvent): void {
  let el: Element | null = e.target as Element;

  while (el && el !== (document as unknown as Element)) {
    // 1. Check for data-tst-rule (injected by element-tagging)
    const ruleId = el.getAttribute(DATA_ATTR_RULE);
    if (ruleId) {
      const ruleEventName = el.getAttribute(DATA_ATTR_RULE_EVENT);
      if (ruleEventName) {
        // once-per-visitor gates the whole dual path here: the pixel would
        // otherwise fire a fresh event_id that never gets a server pair and
        // the platform would count the repeat as a new conversion
        const taggedRule = ruleById(ruleId);
        if (taggedRule && shouldSkipForRule(ruleId, taggedRule.oncePerVisitor)) return;
        const rawValue = el.getAttribute(DATA_ATTR_RULE_VALUE);
        const currency = el.getAttribute(DATA_ATTR_RULE_CURRENCY);
        // Once-per-visitor marking happens inside sendEvent, only when the
        // event actually leaves (a parked event must not burn the rule).
        queueMicrotask(() => sendEvent({
          eventName: ruleEventName,
          eventValue: rawValue ? parseFloat(rawValue) : undefined,
          currency: currency || undefined,
          trackingRuleId: ruleId,
        }));
        return;
      }
    }

    // 2. Check for data-tst-event attribute (manual data attributes)
    const eventName = el.getAttribute(DATA_ATTR_EVENT);
    if (eventName && el.tagName !== "FORM") {
      const data = parseDataAttributes(el);
      queueMicrotask(() => sendEvent(data as Parameters<typeof sendEvent>[0]));
      return;
    }

    // 3. Check config-based click rules (elementText fallback for old rules)
    if (!eventName) {
      const rule = findClickRule(el);
      if (rule) {
        if (shouldSkipForRule(rule.id, rule.oncePerVisitor)) return;
        queueMicrotask(() => sendEvent({
          eventName: rule.eventName,
          eventValue: rule.eventValue,
          currency: rule.currency,
          trackingRuleId: rule.id,
        }));
        return;
      }
    }

    el = el.parentNode as Element | null;
  }
}

/** Handle form submit events — checks data attributes first, then config rules. */
function handleFormSubmit(e: SubmitEvent): void {
  const form = e.target as HTMLFormElement;
  if (!form || form.tagName !== "FORM") return;
  if (isNonConversionForm(form)) return;

  // Mark as natively submitted so AJAX detection can skip this form
  markNativeSubmit(form);

  const eventName = form.getAttribute(DATA_ATTR_EVENT);
  let data: Record<string, unknown> | null = null;
  let detectEmail = false;

  if (eventName) {
    // Data-attribute driven form event
    data = parseDataAttributes(form);
    detectEmail = form.getAttribute(DATA_ATTR_DETECT_EMAIL) === "true";
  } else {
    // Config-rule driven form event
    const rule = findFormSubmitRule();
    if (rule) {
      if (shouldSkipForRule(rule.id, rule.oncePerVisitor)) return;
      data = {
        eventName: rule.eventName,
        eventValue: rule.eventValue,
        currency: rule.currency,
        trackingRuleId: rule.id,
      };
      // Deliberately NOT marked as fired here.
      //
      // Marking before the form's own verdict burns a once-per-visitor rule on
      // a submission that was rejected: the visitor fixes the error, submits
      // again, and that attempt is skipped as already counted. The lead is then
      // lost for that person for good. sendEvent itself marks the rule at the
      // moment the event actually leaves the browser.
      detectEmail = rule.detectEmail !== false;
    }
  }

  // No matching rule — capture user data and send if we found email/phone.
  // This ensures match quality data reaches the backend before page navigation.
  // Hashing is started here but never waited for before deciding to hold.
  // Contact Form 7 answers in about 170ms; if the wait were armed only after
  // the hashes resolved, that verdict could arrive while nothing was waiting
  // for it, be discarded, and the conversion would then be counted by the
  // timeout anyway. Arm first, send inside the closure once the hashes are in.
  if (!data) {
    const prevEm = state.userData.em;
    const prevPh = state.userData.ph;
    const captured = captureFormUserData(form);
    submitOrHold(e, () => {
      captured.then(() => {
        const capturedNew = state.userData.em !== prevEm || state.userData.ph !== prevPh;
        if (capturedNew) sendEvent({ source: "auto_capture" });
      });
    });
    return;
  }

  // Once-per-visitor marking lives inside sendEvent, at the moment the event
  // actually leaves the browser: a rejected submission or a consent-parked
  // event does not consume the rule.
  const send = () => {
    sendEvent(data as Parameters<typeof sendEvent>[0]);
  };

  if (detectEmail) {
    const captured = captureFormUserData(form);
    submitOrHold(e, () => {
      // Await hashing before sending so userData.em/ph are populated
      captured.then(send);
    });
    return;
  }

  submitOrHold(e, send);
}

/**
 * Report this submission now, or hold it until the form says what came of it.
 *
 * The distinction is `defaultPrevented`, read one macrotask after dispatch so
 * the page's own handler has had its turn (ours runs in the capture phase, so
 * reading it immediately would always say false).
 *
 * Not prevented means the browser is navigating away, which is a submission
 * that really happened; sending at once is also safer there, because the page
 * is about to go.
 *
 * Prevented means a framework took over and the outcome is still unknown. This
 * is the case that used to be counted regardless: Contact Form 7 carries
 * `novalidate`, so its submit event fires even for a submission the server then
 * rejects with `wpcf7invalid`. Every one of those was somebody's Lead.
 */
function submitOrHold(e: SubmitEvent, send: () => void): void {
  setTimeout(() => {
    if (!e.defaultPrevented) {
      send();
      return;
    }
    armPendingSubmission(send);
  }, 0);
}

/** Install click and form submit event delegation listeners. */
export function installDomListeners(): void {
  document.addEventListener("click", handleClickEvent, true);
  document.addEventListener("submit", handleFormSubmit, true);
}
